/**
 * Area structure registry (dockview mechanism) plus the static constants and
 * spec-derived predicates it drives (架构方案 §5.7).
 *
 * Two config layers, both keyed by `LayoutArea`:
 *   - {@link AREA_SPECS} (here) — dockview *mechanism*: `host` (where it lives),
 *     placement, custom tab, and the drag/resize/drop capability flags. Static,
 *     renderer-owned.
 *   - the `LayoutSchema` from `@hibro/shared` — business *policy*: `enabled` /
 *     `visible` / `size`. This is what a build / role / permission overlay edits.
 */
import type { LayoutArea } from '@hibro/shared';
import type { DockviewPanelRenderer } from 'dockview-react';

import { RAIL_LABEL_SIZE } from '../activity/rail-mode';
import type { ToggleableArea } from '../layout-context';

/** dockview dock direction (relative to a reference area, or the grid edge). */
export type AreaDirection = 'left' | 'right' | 'above' | 'below';

/** Edge-group shell position for pinned chrome (outside the central grid). */
export type EdgePosition = 'top' | 'bottom' | 'left' | 'right';

/**
 * Where an area is hosted. This is the only *structural* axis — it selects the
 * dockview mechanism and is what the build / configure / visibility code paths
 * branch on. It is deliberately **decoupled from behaviour**: an area's
 * drag / resize / drop are independent per-area flags (see {@link AreaSpec}), so
 * changing what an area can do never requires changing (or inventing) a host.
 *
 *   - `grid`  in the central gridview (`addPanel`, gridview size constraints,
 *             group `setVisible`). The recursive, dockable region.
 *   - `edge`  pinned in a shell edge slot *outside* the gridview (`addEdgeGroup`,
 *             shell-owned size, `setEdgeGroupVisible`). Keeps the gridview's
 *             root edges clean so a dropped bar never spans the chrome.
 */
export type AreaHost = 'grid' | 'edge';

/**
 * Per-area *structural + behavioural* spec — pure dockview mechanism, owned by
 * the renderer (架构方案 §5.7). Kept separate from the schema's business policy
 * (`enabled` / `visible` / `size`, see `@hibro/shared`): these fields are static
 * per area and never part of a permission/role overlay. Both are keyed by
 * `LayoutArea` so an effective layout is a simple merge.
 *
 * Design: `host` (structural, stable) and the capability flags (behavioural,
 * requirement-driven) are orthogonal. Capabilities are free per-area config —
 * each defaults to `true` (fully interactive) and is locked by setting it
 * `false`, so any of the 2³ = 8 drag/resize/drop combinations is expressible on
 * any host without new types. Future requirement changes touch only the flags.
 */
export interface AreaSpec {
  /** Hosting container — the structural axis (see {@link AreaHost}). */
  host: AreaHost;
  /**
   * Placement when first created for `grid` areas. Omit for the flexible
   * `editor` anchor; relative regions set `referenceArea` to dock next to that
   * area's group; a `referenceArea`-less direction docks against the central
   * grid edge (used by activityBar). Unused for `edge` areas (their slot is
   * `edge`). The sizing axis (width vs height) is derived from `direction`.
   */
  position?: { direction: AreaDirection; referenceArea?: LayoutArea };
  /**
   * Required for `host: 'edge'`: which shell slot to pin the bar in. The shell
   * owns the bar's size / min / max (applied at creation), not gridview
   * constraints.
   */
  edge?: EdgePosition;
  /**
   * Custom tab-component id (renderer registry). Used by `panel` and by the
   * tabbed area's collapsed structural anchor; instance tabs use `surfaceTab`.
   */
  tabComponent?: string;
  /**
   * Drag to move. Default: true. When false the group can't be dragged (a
   * `onWillDrag*` veto cancels the native drag). By default a non-draggable area
   * also hides its tab header (the header is the drag handle); set
   * {@link showHeader} to keep the tab visible while still locking the drag.
   */
  draggable?: boolean;
  /**
   * Force the tab header (tab strip) visible independent of {@link draggable}.
   * Defaults to `draggable` — a non-draggable area hides its header. Set `true`
   * to show the tab on a locked area (e.g. activityBar): the header renders but
   * drag is still vetoed and drops are still blocked via `droppable`.
   */
  showHeader?: boolean;
  /**
   * Narrow {@link showHeader} for a tabbed area: keep the strip down until the
   * area actually holds instance tabs, so an `editor` with only its singleton
   * view looks exactly as it did before the area was tabbed.
   *
   * `auxiliaryBar` deliberately does *not* set this — its strip carries the
   * add-tab / hide-area actions, which have to stay reachable when empty.
   */
  hideHeaderWhenEmpty?: boolean;
  /** Resize via splitter (pins size when false). Default: true. */
  resizable?: boolean;
  /**
   * Preserve the area's current cross-axis size while dockview performs a
   * structural mutation. The area is pinned only for the mutation transaction,
   * then its normal bounds are restored so sash resizing remains available.
   * Applies only to resizable central-grid areas with a sizing axis.
   */
  preserveSizeOnMutation?: boolean;
  /** Accept dropped panels (no-drop-target when false). Default: true. */
  droppable?: boolean;
  /**
   * How dockview renders this area's anchor panel while it is not its group's
   * active one. Default (`onlyWhenVisible`) takes the panel's content out of the
   * document, which is fine for an area that is alone in its group — but a
   * *tabbed* area's anchor shares the group with instance tabs, and the `editor`
   * anchor carries the singleton view plus the whole MRU keep-alive cache
   * (§5.7 视图缓存). Activating any document tab would otherwise tear that DOM
   * out (iframes reload, scroll resets), so tabbed anchors declare `always`.
   *
   * A **capability** field, not a creation option: it is applied in
   * `configureAreas` via `panel.api.setRenderer` on every restore, so an
   * existing serialized tree self-heals instead of needing a
   * {@link LAYOUT_STORAGE_KEY} bump (`renderer` is part of a panel's serialized
   * state, so routing it through `addPanel` would need one).
   */
  renderer?: DockviewPanelRenderer;
  /**
   * Cross-axis bounds (px) applied while `resizable`. For the `editor` anchor
   * (no dock direction, hence no cross-axis) these are applied as *width* bounds
   * on the center column instead (see `configureAreas`).
   */
  minSize?: number;
  maxSize?: number;
  /**
   * Class on the area's splitview `.dv-view` (grid) or `.dv-groupview` (edge).
   * Defaults to `app-area-{area}-view`. Used for column-level CSS (padding,
   * sash gaps, transparent chrome). Applied in `applyAreaViewClasses`.
   */
  viewClass?: string;
  /**
   * Class on the `.dv-sash` immediately before this area's column (horizontal
   * splits). Margin/padding values live in CSS (`dockview-theme.css`).
   */
  leadingSash?: boolean;
  /**
   * Class on the `.dv-sash` immediately after this area's column (horizontal
   * splits). Used when the platform owns that sash (e.g. activityBar rail).
   */
  trailingSash?: boolean;
  /**
   * Class on the outer `.dv-view` that wraps a nested branch split rooted at
   * this area (e.g. the editor/panel column). Only applied when a
   * `.dv-branch-node` ancestor exists between the panel leaf and that view.
   */
  branchViewClass?: string;
}

/**
 * Structural registry for every area (架构方案 §5.7; see file header for the
 * overall model). The default center tree —
 *
 *   center (gridview root, horizontal):
 *     [ activityBar, sidebar, [editor, panel], auxiliaryBar ]
 *
 * `editor` is the flexible anchor; `sidebar` / `auxiliaryBar` dock to its sides
 * and `panel` below it (the editor/panel vertical sub-split — the "twoCenter"
 * container). `activityBar` is the locked leftmost column, next to the sidebar.
 * Each area declares its `host` plus only the capabilities it wants to lock —
 * everything else defaults to interactive, and splits nest unlimited via group
 * content drop targets + single-child auto-collapse.
 *
 * Gotcha — the shell (dockview's ShellManager) has a fixed nesting: the outer
 * splitview is horizontal `[left, middleColumn, right]` and the middle column is
 * vertical `[top, center, bottom]`. We use only the top/bottom slots, so
 * header / statusBar span full width; left/right slots are unused.
 */
export const AREA_SPECS: Record<LayoutArea, AreaSpec> = {
  // Locked leftmost rail: no drag / no drop. Magnetic two-stop only
  // (60 icon / 162 icon+title). max equals the label stop so the sash
  // cannot rest wider. See `rail-snap.ts`.
  activityBar: {
    host: 'grid',
    position: { direction: 'left' },
    draggable: false,
    droppable: false,
    preserveSizeOnMutation: true,
    trailingSash: true,
    minSize: 60,
    maxSize: RAIL_LABEL_SIZE,
  },
  sidebar: {
    host: 'grid',
    minSize: 260,
    maxSize: 480,
    position: { direction: 'left', referenceArea: 'editor' },
  },
  // Dockview-tabbed area too (§5.7 / 见 20 二期): document-like view instances
  // open here as native `surfaceTab`s, one tab per document. `showHeader` keeps
  // the strip visible while `draggable: false` still locks the area itself from
  // being dragged away; the structural anchor's own tab is collapsed via the
  // `areaAnchor` tab component + CSS, so an editor with no open instances looks
  // exactly as it did before.
  editor: {
    host: 'grid',
    branchViewClass: 'app-area-twoCenter-view',
    draggable: false,
    droppable: true,
    showHeader: true,
    hideHeaderWhenEmpty: true,
    tabComponent: 'areaAnchor',
    renderer: 'always',
  },
  panel: {
    host: 'grid',
    position: { direction: 'below', referenceArea: 'editor' },
    tabComponent: 'panel',
  },
  // Dockview-tabbed area: view instances (§5.7 视图多实例) open as native tabs
  // (surfaceTab). A structural anchor panel keeps the group/visibility machinery
  // alive with zero open instances; its own tab is collapsed via the `areaAnchor`
  // tab component + CSS so the strip shows only instance tabs.
  auxiliaryBar: {
    host: 'grid',
    position: { direction: 'right', referenceArea: 'editor' },
    leadingSash: true,
    droppable: true,
    draggable: true,
    showHeader: true,
    tabComponent: 'areaAnchor',
    renderer: 'always',
    minSize: 324,
    maxSize: 532,
  },
  // Full-width locked chrome via the shell's top / bottom slots (outside the grid).
  header: {
    host: 'edge',
    edge: 'top',
    draggable: false,
    resizable: false,
    droppable: false,
  },
  statusBar: {
    host: 'edge',
    edge: 'bottom',
    draggable: false,
    resizable: false,
    droppable: false,
  },
};

// ── Constants ─────────────────────────────────────────────────────────────────

/**
 * localStorage key for the serialized dockview tree. Bump the version suffix
 * whenever the stored schema changes in a backward-incompatible way so that
 * stale layouts are discarded and rebuilt from the default.
 *
 * `v2`: the `editor` area became a tabbed host (§5.7 / 见 20 二期), which gave its
 * structural anchor panel a `tabComponent`. Panel creation options **are**
 * serialized, so a `v1` tree would restore the anchor without one — dockview
 * would then draw it as an ordinary titled tab **with a close button**, and
 * closing it removes the panel that hosts the whole editor area (nothing
 * recreates it; `reconcileEnabledAreas` skips `editor` by design). Discarding
 * v1 trees is the migration.
 */
export const LAYOUT_STORAGE_KEY = 'app.layout.dockview.v2';

/** Structural mutations that may redistribute central-grid split sizes. */
export const SIZE_CHANGING_MUTATIONS: ReadonlySet<string> = new Set([
  'add',
  'remove',
  'move',
  'float',
  'popout',
]);

/**
 * Which central-grid *root* (outer-edge) drop directions are accepted.
 *
 * dockview's `dndEdges` only toggles all four root edges on/off (no per-edge
 * option), so directional gating is done by vetoing unwanted `kind: 'edge'`
 * overlays in `onWillShowOverlay` (see `useDockviewManager`). Because the chrome
 * (header / statusBar) lives in the edge-group shell *outside* the gridview,
 * these are the clean outer edges of the center — currently left/right only;
 * top/bottom are temporarily disabled. Add `'top'` / `'bottom'` here to re-enable.
 */
export const ROOT_DOCK_EDGES: ReadonlySet<EdgePosition> = new Set<EdgePosition>(
  ['left', 'right'],
);

/** A group-content overlay direction (four quadrants + `center` = drop-as-tab). */
export type ContentDropPosition =
  | 'left'
  | 'right'
  | 'top'
  | 'bottom'
  | 'center';

/**
 * Per-area docking policy for *within-group* content overlays (`kind: 'content'`):
 * a panel/group dropped onto a gated area's group is accepted only in these
 * directions (see `onWillShowOverlay` in `useDockviewManager`). Both the editor
 * and the auxiliaryBar accept the four split directions plus `center`
 * (drop-as-tab). Areas absent here are ungated (default dockview behaviour), and
 * tab-strip drops (`kind: 'tab' | 'header_space'`) are never gated by direction.
 *
 * Resolution is by anchor identity: each grid area owns a panel whose id === the
 * area name (`addAreaPanel`), so the target group maps back to its area via
 * `api.getPanel(area)?.group === e.group`.
 */
export const AREA_DROP_POSITIONS: Partial<
  Record<LayoutArea, ReadonlySet<ContentDropPosition>>
> = {
  editor: new Set<ContentDropPosition>([
    'left',
    'right',
    'top',
    'bottom',
    'center',
  ]),
  auxiliaryBar: new Set<ContentDropPosition>([
    'left',
    'right',
    'top',
    'bottom',
    'center',
  ]),
};

/** Areas listed in {@link AREA_DROP_POSITIONS}, precomputed for the drop gate. */
export const DROP_GATED_AREAS: readonly LayoutArea[] = Object.keys(
  AREA_DROP_POSITIONS,
) as LayoutArea[];

/** Last-resort size (px) when a sized area omits `size` in the schema. */
export const DEFAULT_AREA_SIZE = 100;

/**
 * The areas a user can show / hide via a header toggle — the only areas a
 * module's `hiddenAreas` blacklist may default to hidden (§5.7). Kept in sync
 * with the `ToggleableArea` union in `layout-context`. Listing any other
 * (load-bearing chrome) area in a module manifest is ignored, since there is no
 * control to reveal it again.
 */
export const TOGGLEABLE_AREAS: readonly ToggleableArea[] = [
  'panel',
  'statusBar',
  'auxiliaryBar',
];

/** Narrow a `LayoutArea` to a toggleable one (module hide only applies here). */
export function isToggleableArea(area: LayoutArea): area is ToggleableArea {
  return (TOGGLEABLE_AREAS as readonly LayoutArea[]).includes(area);
}

/**
 * Order in which areas are added to a fresh layout. The `editor` anchor must
 * come first; relative regions (sidebar / auxiliaryBar / panel) attach to it;
 * `activityBar` docks last to the central grid's left edge. `header` /
 * `statusBar` are edge-group chrome (created in their own shell slots, order
 * within the grid does not matter). This is a dockview construction concern
 * (corner ownership depends on add order) and is intentionally distinct from the
 * schema's declaration order.
 */
export const AREA_BUILD_ORDER: readonly LayoutArea[] = [
  'editor',
  'sidebar',
  'auxiliaryBar',
  'panel',
  'activityBar',
  'header',
  'statusBar',
];

// ── Spec-derived predicates ───────────────────────────────────────────────────

/** Whether an area is hosted in the edge-group shell (vs the central grid). */
export function isEdgeArea(area: LayoutArea): boolean {
  return AREA_SPECS[area].host === 'edge';
}

/** Sizing axis for a *central-grid* area, derived from its dock direction. */
export function axisOf(area: LayoutArea): 'width' | 'height' | undefined {
  const dir = AREA_SPECS[area].position?.direction;
  if (dir === 'left' || dir === 'right') {
    return 'width';
  }
  if (dir === 'above' || dir === 'below') {
    return 'height';
  }
  return undefined;
}

/**
 * Resolve an area's three interaction capabilities — each is a free per-area
 * flag defaulting to `true` (fully interactive); an area locks a capability by
 * declaring it `false` (架构方案 §5.7). Independent + requirement-driven, so any
 * of the 8 drag/resize/drop combinations is expressible without new types.
 *
 * The flags map to three orthogonal dockview mechanisms (drag = hide the tab
 * header; resize = min/max constraints; drop = `no-drop-target` lock). For
 * `grid` areas the constraints are gridview constraints; for `edge` areas the
 * size / resize bounds are owned by the edge-group shell (set at creation), so
 * only `draggable` (header) and `droppable` (lock) are applied post-hoc.
 */
export function areaCaps(area: LayoutArea): {
  draggable: boolean;
  resizable: boolean;
  droppable: boolean;
} {
  const spec = AREA_SPECS[area];
  return {
    draggable: spec.draggable ?? true,
    resizable: spec.resizable ?? true,
    droppable: spec.droppable ?? true,
  };
}

/**
 * Areas that lock drag (`draggable: false`) — their group must never become the
 * source of a dockview drag, even when the tab strip is shown (`showHeader`).
 *
 * Exposed as the locked *set* rather than a per-id predicate because the veto
 * that consumes it (`useDockviewManager`) runs from a capture-phase `dragstart`
 * listener, before dockview has resolved the gesture to a panel or group id.
 */
export const DRAG_LOCKED_AREAS: readonly LayoutArea[] = (
  Object.keys(AREA_SPECS) as LayoutArea[]
).filter((area) => !areaCaps(area).draggable);
